import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Mic, Square, Loader2, HelpCircle, X, Check, Pencil, AlertTriangle } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getLocalTodayYMD, formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { useMicVolumeMeter } from "@/hooks/useMicVolumeMeter";
import MicVolumeMeter from "@/components/ai-post/MicVolumeMeter";

export interface VoicePostFields {
  origin_city?: string | null;
  origin_state?: string | null;
  destination_city?: string | null;
  destination_state?: string | null;
  equipment_type?: string | null;
  weight_lbs?: number | null;
  rate?: number | null;
  pickup_date?: string | null;
  commodity?: string | null;
  notes?: string | null;
}

type VoiceState = "idle" | "recording" | "processing" | "done" | "error";

interface VoicePostCardProps {
  onPostLoad?: (fields: VoicePostFields) => void;
  isPosting?: boolean;
}

type EditableFieldKey = "origin" | "destination" | "equipment" | "date" | "weight" | "rate";

const GUIDE_FIELDS: {
  key: EditableFieldKey;
  label: string;
  check: (f: VoicePostFields) => boolean;
  value: (f: VoicePostFields) => string | null;
}[] = [
  { key: "origin", label: "Origin", check: (f) => !!f.origin_city, value: (f) => f.origin_city && f.origin_state ? `${f.origin_city}, ${f.origin_state}` : f.origin_city || null },
  { key: "destination", label: "Destination", check: (f) => !!f.destination_city, value: (f) => f.destination_city && f.destination_state ? `${f.destination_city}, ${f.destination_state}` : f.destination_city || null },
  { key: "equipment", label: "Equipment", check: (f) => !!f.equipment_type, value: (f) => f.equipment_type || null },
  { key: "date", label: "Date", check: (f) => !!f.pickup_date, value: (f) => f.pickup_date ? formatYYYYMMDDToLocaleDateString(f.pickup_date, undefined, { month: "short", day: "numeric" }) : null },
  { key: "weight", label: "Weight", check: (f) => !!f.weight_lbs, value: (f) => f.weight_lbs ? `${(f.weight_lbs / 1000).toFixed(0)}k lbs` : null },
  { key: "rate", label: "Rate", check: (f) => !!f.rate, value: (f) => f.rate ? `$${f.rate.toLocaleString()}` : null },
];

const COMMON_EQUIPMENT = ["Reefer", "Dry Van", "Flatbed", "Step Deck", "Van", "Power Only", "Conestoga", "Lowboy", "Hotshot"];

function getSanityWarnings(f: VoicePostFields): string[] {
  const warnings: string[] = [];
  if (f.weight_lbs && f.weight_lbs < 1000) warnings.push("Weight seems very low — did you mean a rate?");
  if (f.rate && f.rate < 100) warnings.push("Rate seems very low — did you mean weight?");
  if (f.rate && f.rate > 50000) warnings.push("Rate seems high — please double-check");
  if (f.weight_lbs && f.weight_lbs > 80000) warnings.push("Weight exceeds typical truck limits");
  return warnings;
}

export default function VoicePostCard({ onPostLoad, isPosting }: VoicePostCardProps) {
  const { toast } = useToast();
  const [state, setState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState("");
  const [fields, setFields] = useState<VoicePostFields>({ pickup_date: getLocalTodayYMD() });
  const [errorMsg, setErrorMsg] = useState("");
  const [showHints, setShowHints] = useState(false);
  const [editingField, setEditingField] = useState<EditableFieldKey | null>(null);
  const [editValue, setEditValue] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const micLevel = useMicVolumeMeter(activeStream, state === "recording");

  /** Safety cap: recordings auto-stop and get processed after this long. */
  const MAX_RECORDING_SECONDS = 90;
  const [secondsLeft, setSecondsLeft] = useState(MAX_RECORDING_SECONDS);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRecordingTimers = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const effectiveFields = useMemo(() => ({
    pickup_date: getLocalTodayYMD(),
    ...fields,
  }), [fields]);

  const sanityWarnings = useMemo(() => state === "done" ? getSanityWarnings(effectiveFields) : [], [state, effectiveFields]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        clearRecordingTimers();
        stream.getTracks().forEach((t) => t.stop());
        setActiveStream(null);
        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          // Reset to clean idle without firing extraction.
          setState("idle");
          setTranscript("");
          setFields({ pickup_date: getLocalTodayYMD() });
          setErrorMsg("");
          setEditingField(null);
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        await processAudio(blob);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setActiveStream(stream);
      setState("recording");

      // Safety cap — auto-stop at 90s and process whatever was captured.
      clearRecordingTimers();
      setSecondsLeft(MAX_RECORDING_SECONDS);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      autoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          cancelledRef.current = false;
          mediaRecorderRef.current.stop();
          setState("processing");
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } catch (err) {
      console.error("Mic access error:", err);
      toast({
        variant: "destructive",
        title: "Microphone access denied",
        description: "Please allow microphone access to use Voice Post.",
      });
    }
  }, [toast, clearRecordingTimers]);

  const stopRecording = useCallback(() => {
    clearRecordingTimers();
    if (mediaRecorderRef.current?.state === "recording") {
      cancelledRef.current = false;
      mediaRecorderRef.current.stop();
      setState("processing");
    }
  }, [clearRecordingTimers]);

  const cancelRecording = useCallback(() => {
    clearRecordingTimers();
    if (mediaRecorderRef.current?.state === "recording") {
      cancelledRef.current = true;
      mediaRecorderRef.current.stop();
      // onstop handler will reset state cleanly
    }
  }, [clearRecordingTimers]);

  // Release the mic if the card unmounts / user navigates away mid-recording.
  useEffect(() => {
    return () => {
      clearRecordingTimers();
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== "inactive") {
        cancelledRef.current = true;
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    };
  }, [clearRecordingTimers]);

  const processAudio = async (blob: Blob) => {
    try {
      const formData = new FormData();
      formData.append("audio", blob, blob.type.includes("mp4") ? "recording.m4a" : "recording.webm");
      // Stale-date fix: send the speaker's real local today + timezone so the
      // parser resolves relative dates ("tomorrow", "Monday") correctly.
      formData.append("client_date", getLocalTodayYMD());
      formData.append("client_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");

      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      };
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-post`,
        { method: "POST", headers, body: formData }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `Error ${res.status}`);
      }

      const data = await res.json();
      setTranscript(data.transcript || "");
      setFields((prev) => ({ ...prev, ...(data.fields || {}) }));
      setState("done");
    } catch (err) {
      console.error("Voice post error:", err);
      const msg = err instanceof Error ? err.message : "Processing failed";
      setErrorMsg(msg);
      setState("error");
      toast({ variant: "destructive", title: "Voice Post failed", description: msg });
    }
  };

  const reset = () => {
    setState("idle");
    setTranscript("");
    setFields({ pickup_date: getLocalTodayYMD() });
    setErrorMsg("");
    setEditingField(null);
  };

  const handlePostLoad = () => {
    onPostLoad?.(effectiveFields);
  };

  const startEditing = (key: EditableFieldKey) => {
    if (state !== "done") return;
    setEditingField(key);
    // Pre-fill edit value with current raw value
    switch (key) {
      case "origin":
        setEditValue(effectiveFields.origin_city && effectiveFields.origin_state ? `${effectiveFields.origin_city}, ${effectiveFields.origin_state}` : effectiveFields.origin_city || "");
        break;
      case "destination":
        setEditValue(effectiveFields.destination_city && effectiveFields.destination_state ? `${effectiveFields.destination_city}, ${effectiveFields.destination_state}` : effectiveFields.destination_city || "");
        break;
      case "equipment":
        setEditValue(effectiveFields.equipment_type || "");
        break;
      case "date":
        setEditValue(effectiveFields.pickup_date || "");
        break;
      case "weight":
        setEditValue(effectiveFields.weight_lbs ? String(effectiveFields.weight_lbs) : "");
        break;
      case "rate":
        setEditValue(effectiveFields.rate ? String(effectiveFields.rate) : "");
        break;
    }
  };

  const saveEdit = () => {
    if (!editingField) return;
    const trimmed = editValue.trim();
    setFields((prev) => {
      const next = { ...prev };
      switch (editingField) {
        case "origin": {
          const parts = trimmed.split(",").map((s) => s.trim());
          next.origin_city = parts[0] || null;
          next.origin_state = parts[1] || prev.origin_state || null;
          break;
        }
        case "destination": {
          const parts = trimmed.split(",").map((s) => s.trim());
          next.destination_city = parts[0] || null;
          next.destination_state = parts[1] || prev.destination_state || null;
          break;
        }
        case "equipment":
          next.equipment_type = trimmed || null;
          break;
        case "date":
          next.pickup_date = trimmed || null;
          break;
        case "weight":
          next.weight_lbs = trimmed ? Number(trimmed.replace(/[^0-9]/g, "")) || null : null;
          break;
        case "rate":
          next.rate = trimmed ? Number(trimmed.replace(/[^0-9.]/g, "")) || null : null;
          break;
      }
      return next;
    });
    setEditingField(null);
    setEditValue("");
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  return (
    <PostLoadCard className="flex flex-col">
      <PostLoadCardHeader
        icon={<Mic className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="Voice Post"
        demoHintKey="section.voicePost"
        variant="atlas"
      >
        {state !== "idle" && (
          <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={reset}>
            <X className="h-4 w-4" />
          </button>
        )}
        {state === "idle" && (
          <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowHints(true)}>
            <HelpCircle className="h-4 w-4" />
          </button>
        )}
      </PostLoadCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-3">
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {state === "idle" && "Say your load info — origin, destination, equipment, date, weight, rate. We'll fill what you mention."}
          {state === "recording" && "Listening... describe your load, then tap to stop."}
          {state === "processing" && "Processing your audio with Atlas AI..."}
          {state === "done" && "Review below. Tap any chip to edit, then Post Load."}
          {state === "error" && (errorMsg || "Something went wrong. Try again.")}
        </p>

        <div className="flex flex-col items-center gap-2 py-2">
          <div className="relative">
            {state === "recording" && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  border: "2px solid hsl(0 72% 51% / 0.5)",
                  animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite",
                }}
              />
            )}
            <button
              onClick={state === "recording" ? stopRecording : state === "idle" || state === "error" ? startRecording : undefined}
              disabled={state === "processing"}
              className="relative h-14 w-14 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  state === "recording"
                    ? "hsl(0 72% 51%)"
                    : "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))",
                boxShadow:
                  state === "recording"
                    ? "0 0 18px 4px hsl(0 72% 51% / 0.35)"
                    : "0 0 18px 4px hsl(170 85% 32% / 0.28)",
              }}
            >
              {state === "processing" ? (
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              ) : state === "recording" ? (
                <Square className="h-5 w-5 text-white fill-white" />
              ) : (
                <Mic className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
          {state === "recording" && (
            <MicVolumeMeter level={micLevel} />
          )}
          {state === "recording" && (
            <span
              className="text-[11px] font-semibold tabular-nums"
              style={{ color: secondsLeft <= 5 ? "hsl(0 72% 51%)" : undefined }}
            >
              0:{String(secondsLeft).padStart(2, "0")} left
              <span className="ml-1 font-normal text-muted-foreground">
                · auto-stops at {MAX_RECORDING_SECONDS}s
              </span>
            </span>
          )}
          <span className="text-[11px] text-muted-foreground italic">
            {state === "idle" && `Tap to start · auto-stops at ${MAX_RECORDING_SECONDS}s`}
            {state === "recording" && "Listening — tap to stop"}
            {state === "processing" && "Analyzing..."}
            {state === "done" && "Complete"}
            {state === "error" && "Tap to retry"}
          </span>
        </div>

        {/* Transcript area */}
        {(state === "done" || state === "processing") && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-foreground leading-relaxed min-h-[40px]">
            {state === "processing" ? (
              <span className="text-muted-foreground italic">Transcribing...</span>
            ) : (
              `"${transcript}"`
            )}
          </div>
        )}

        {state === "idle" && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground leading-relaxed italic">
            "Reefer from Nogales AZ to Bronx NY, 42k lbs, picking up Monday at $3,200..."
          </div>
        )}

        {/* Guided field chips — tappable in done state */}
        <div className="flex flex-wrap gap-1.5">
          {GUIDE_FIELDS.map((gf) => {
            const filled = gf.check(effectiveFields);
            const displayValue = filled ? gf.value(effectiveFields) : null;
            const isEditing = editingField === gf.key;
            return (
              <Popover key={gf.key} open={isEditing} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onClick={() => state === "done" && startEditing(gf.key)}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium transition-all duration-300 ${
                      state === "done" ? "cursor-pointer hover:ring-1 hover:ring-ring/50" : "cursor-default"
                    } ${
                      filled
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                        : "text-muted-foreground border-border bg-transparent"
                    }`}
                  >
                    {filled && <Check className="h-3 w-3 mr-1 shrink-0" />}
                    {displayValue || gf.label}
                    {state === "done" && <Pencil className="h-2.5 w-2.5 ml-1 opacity-40" />}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" side="bottom" align="start">
                  <div className="space-y-2">
                    <label className="text-[11px] font-medium text-foreground">{gf.label}</label>
                    {gf.key === "equipment" ? (
                      <select
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      >
                        <option value="">Select...</option>
                        {COMMON_EQUIPMENT.map((eq) => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                        {EQUIPMENT_FLAT_LIST.filter(e => !COMMON_EQUIPMENT.some(c => c.toLowerCase() === e.name.toLowerCase())).slice(0, 20).map((eq) => (
                          <option key={eq.enumValue} value={eq.name}>{eq.name}</option>
                        ))}
                      </select>
                    ) : gf.key === "date" ? (
                      <Input
                        type="date"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="h-8 text-xs"
                      />
                    ) : gf.key === "weight" || gf.key === "rate" ? (
                      <div className="flex items-center gap-1">
                        {gf.key === "rate" && <span className="text-xs text-muted-foreground">$</span>}
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder={gf.key === "weight" ? "e.g. 41000" : "e.g. 6500"}
                          className="h-8 text-xs"
                        />
                        {gf.key === "weight" && <span className="text-xs text-muted-foreground">lbs</span>}
                      </div>
                    ) : (
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder={gf.key === "origin" || gf.key === "destination" ? "City, ST" : ""}
                        className="h-8 text-xs"
                      />
                    )}
                    <div className="flex gap-1.5 justify-end">
                      <button
                        onClick={cancelEdit}
                        className="rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <button
                        onClick={saveEdit}
                        className="rounded px-2 py-1 text-[10px] text-emerald-700 hover:bg-emerald-500/10 transition-colors"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>

        {state === "idle" && (
          <p className="text-[10px] text-muted-foreground">
            Dates: say "today", "Monday", "next Friday", or any date
          </p>
        )}

        {/* Sanity warnings */}
        {state === "done" && sanityWarnings.length > 0 && (
          <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-2 space-y-1">
            {sanityWarnings.map((w, i) => (
              <p key={i} className="text-[10px] text-yellow-700 dark:text-yellow-400 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                {w}
              </p>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-auto space-y-2 pt-1">
          {state === "recording" ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={cancelRecording}
                className="w-full rounded-md py-2 text-xs font-medium border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={stopRecording}
                className="w-full rounded-md py-2 text-xs font-semibold text-white transition-colors"
                style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
              >
                Stop & Process
              </button>
            </div>
          ) : state === "done" && onPostLoad ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={reset}
                disabled={isPosting}
                className="w-full rounded-md py-2 text-xs font-medium border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                Start Over
              </button>
              <button
                onClick={handlePostLoad}
                disabled={isPosting}
                className="w-full rounded-md py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
              >
                {isPosting ? "Posting..." : "Post Load"}
              </button>
            </div>
          ) : state === "error" ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={reset}
                className="w-full rounded-md py-2 text-xs font-medium border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Start Over
              </button>
              <button
                onClick={startRecording}
                className="w-full rounded-md py-2 text-xs font-semibold text-white transition-colors"
                style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
              >
                Try Again
              </button>
            </div>
          ) : state === "idle" ? (
            <button
              onClick={startRecording}
              className="w-full rounded-md py-2 text-xs font-semibold text-white transition-colors"
              style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
            >
              Start Recording
            </button>
          ) : null}
        </div>
      </div>

      <Dialog open={showHints} onOpenChange={setShowHints}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Voice Post — What to Say</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-[12px] leading-relaxed">
            <section>
              <h4 className="font-semibold text-foreground mb-1.5 text-[13px]">Quick Examples</h4>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="bg-muted/50 rounded-md px-2.5 py-1.5 italic">"Reefer from McAllen Texas to Bronx New York, 41 thousand pounds, sixty-five hundred dollars"</li>
                <li className="bg-muted/50 rounded-md px-2.5 py-1.5 italic">"Dry van, Dallas to Chicago, picking up Monday, 38k lbs at $2,800"</li>
                <li className="bg-muted/50 rounded-md px-2.5 py-1.5 italic">"Flatbed from Houston to Miami, 44 thousand pounds, rate is $4,200, picking up tomorrow"</li>
                <li className="bg-muted/50 rounded-md px-2.5 py-1.5 italic">"I need a step deck, Laredo TX to Atlanta GA, 42k lbs"</li>
                <li className="bg-muted/50 rounded-md px-2.5 py-1.5 italic">"Produce load, reefer, Nogales Arizona to New York, 40 thousand pounds at $7,500"</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-foreground mb-1.5 text-[13px]">Field-by-Field Guide</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Origin</Badge>
                <span>"from McAllen Texas" or "from Dallas TX"</span>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Destination</Badge>
                <span>"to Bronx New York" or "going to Chicago"</span>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Equipment</Badge>
                <span>"reefer", "dry van", "flatbed", "step deck"</span>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Date</Badge>
                <span>"today", "tomorrow", "Monday", "next Friday", "March 28th"</span>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Weight</Badge>
                <span>"41 thousand pounds", "38k lbs", "42,000 pounds"</span>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium bg-emerald-500/15 text-emerald-700 border-emerald-500/30 justify-self-start">Rate</Badge>
                <span>"$6,500", "sixty-five hundred", "rate is 3200"</span>
              </div>
            </section>

            <section>
              <h4 className="font-semibold text-foreground mb-1.5 text-[13px]">Equipment Types</h4>
              <div className="flex flex-wrap gap-1.5">
                {COMMON_EQUIPMENT.map((eq) => (
                  <Badge key={eq} variant="outline" className="text-[10px] px-2 py-0.5 font-medium text-muted-foreground">{eq}</Badge>
                ))}
              </div>
            </section>

            <section>
              <h4 className="font-semibold text-foreground mb-1.5 text-[13px]">Pro Tips</h4>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Speak naturally — you don't need a specific order</li>
                <li>You don't need all fields — say what you know</li>
                <li>Date defaults to today if you don't mention one</li>
                <li>Say "commodity is produce" or just mention it naturally</li>
                <li>You can add notes like "no touch freight" or "team drivers only"</li>
                <li>After recording, tap any chip to edit before posting</li>
              </ul>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </PostLoadCard>
  );
}
